import Badge from "@mui/material/Badge";
import { alpha } from "@mui/material/styles";
import { useQuery } from "@tanstack/react-query";
import { memo } from "react";
import { IoGift } from "react-icons/io5";

import HeaderIconButton, { headerBadgeSx } from "@/components/Core/HeaderIconButton";
import { MASCOT_PALETTE } from "@/components/Others/MascotPanel";
import { useDialog, useIsLoggedIn, useTranslate } from "@/hooks";
import { getDailySpinStatus } from "@/services";
import { dailySpinStatusKey } from "@/views/Others/DailySpin/data";

/**
 * The daily wheel's button, lit with a gold dot while a spin is waiting.
 * Hidden altogether when no wheel is on offer for this site.
 */
const DailySpinButton = () => {
  const t = useTranslate();
  const isLoggedIn = useIsLoggedIn();
  const { openDialog, activeDialog } = useDialog();

  const { data } = useQuery({
    queryKey: dailySpinStatusKey(isLoggedIn),
    queryFn: getDailySpinStatus,
    staleTime: 60 * 1000,
  });

  if (!data?.enabled) return null;

  const active = activeDialog === "daily-spin";

  return (
    <Badge
      variant="dot"
      overlap="circular"
      invisible={!data.available}
      sx={{
        ...headerBadgeSx(MASCOT_PALETTE.crown),
        "& .MuiBadge-dot": {
          width: 8,
          height: 8,
          borderRadius: "50%",
          // A ring that swells off the dot: a free spin is waiting.
          animation: "daily-spin-dot 2s ease-out infinite",
          "@keyframes daily-spin-dot": {
            "0%": { boxShadow: `0 0 0 0 ${alpha(MASCOT_PALETTE.crown, 0.7)}` },
            "70%, 100%": { boxShadow: `0 0 0 6px ${alpha(MASCOT_PALETTE.crown, 0)}` },
          },
          "@media (prefers-reduced-motion: reduce)": { animation: "none" },
        },
      }}
    >
      <HeaderIconButton
        label={t("header.daily-spin")}
        active={active}
        aria-pressed={active}
        onClick={() => openDialog("daily-spin")}
      >
        <IoGift aria-hidden />
      </HeaderIconButton>
    </Badge>
  );
};

export default memo(DailySpinButton);
